import type React from "react";
import {
  Box,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { Share as ShareIcon, Print as PrintIcon } from "@mui/icons-material";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import type {
  IExpandedCallSheetData,
  ICallSheetPageProps,
  IUser,
} from "../../../../types/callsheet";
import { tokens } from "../../../../theme";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../store/Index";
import { useParams } from "react-router-dom";
import apiService from "../../../../service/Api";
import { toast } from "react-toastify";
import type {
  CallSheetData,
  CallSheetLayoutProps,
  CrewEntry,
  CrewEntryResponse,
} from "./types/callsheet";
import CallSheetHeader from "./CallSheetHeader";
import CallSheetMainInfo from "./CallSheetMainInfo";
import CallSheetSchedule from "./CallSheetSchedule";
import CrewTable from "./CrewTable";
import ClientDetails from "./ClientDetails";
import PrintableCallSheet from "./PrintableCallsheet";
import EventDetailsPage from "./CallSheetEventDetails";
import PrintableEventDetails from "./PrintableEventDetails";
import { useCallback, useEffect, useState, useRef } from "react";
import type { TimeAllocation } from "../../hooks/useStreamData";
import ReviewModal from "./ReviewModal";
import AssignCrewModal from "./AssignCrewModal";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import AssignmentRateDialog from "../../../crew-management/components/AssignmentRateDialog";
import MissingRoleRateDialog from "../../../crew-management/components/MissingRoleRateDialog";
import useAssignmentRateGuard from "../../../crew-management/hooks/useAssignmentRateGuard";
import {
  AssignmentRateResolution,
  createPixelPilotJobAndGig,
} from "../../../crew-management/utils/assignmentRates";

export interface Role {
  id: number;
  name: string;
  description: string;
}

const CallSheetPage: React.FC<ICallSheetPageProps> = () => {
  const [assignCrewModalOpen, setAssignCrewModalOpen] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  type CrewReview = CrewEntry & { role: string };
  const [selectedCrewForReview, setSelectedCrewForReview] =
    useState<CrewReview | null>(null);

  const [callSheetData, setCallSheetData] =
    useState<IExpandedCallSheetData | null>(null);
  const [showAddButton, setShowAddButton] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const { id } = useParams();
  const [crewMembers, setCrewMembers] = useState<CrewEntry[]>([]);
  const [roleOptions, setRoleOptions] = useState<Role[]>([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [timeData, setTimeData] = useState<CallSheetData | null>({
    call_time: "N/A",
    departure_time: "N/A",
    arrival_at_venue_time: "N/A",
    setup_from_time: "N/A",
    setup_to_time: "N/A",
    shoot_start_time: "N/A",
    shoot_end_time: "N/A",
    estimated_wrap_time: "N/A",
    back_to_base_time: "N/A",
    unpacking_time: "N/A",
    depart_time: "N/A",
  });
  const [eventDetails, setEventDetails] = useState({
    priest_name: "",
    eulogist_name: "",
    mc_name: "",
    other: "",
    program: "",
    notes: {
      travel: "",
      catering: "",
      logistics: "",
      technical: "",
      special_requirements: "",
      additional_notes: "",
    },
  });

  // Print ref
  const printRef = useRef<HTMLDivElement>(null);

  const printEventDetailsRef = useRef<HTMLDivElement>(null);
  const {
    ensureRateForRole,
    missingRateDialogProps,
    assignmentRateDialogProps,
  } = useAssignmentRateGuard();

  const fetchRoles = async (page = 1) => {
    try {
      const res = await apiService.get<{
        count: number;
        results: Role[];
      }>(`/api/crews/roles/?page=${page}`);

      setRoleOptions(res.data.results);
    } catch (err) {
      console.error("Failed to load roles", err);
    }
  };

  useEffect(() => {
    fetchRoles(1);
  }, []);

  // Animation variants (keeping your original animations)

  const handlePrintClick = async () => {
    if (!callSheetData || !printRef.current || !printEventDetailsRef.current) {
      toast.error("No data to download");
      return;
    }

    setIsPrinting(true);

    try {
      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const captureOptions = {
        scale: 1.5,
        useCORS: true,
        backgroundColor: "#ffffff",
      };

      const canvas1 = await html2canvas(printRef.current, captureOptions);
      const imgData1 = canvas1.toDataURL("image/jpeg", 0.7);

      pdf.addImage(imgData1, "JPEG", 0, 0, pdfWidth, pdfHeight);

      pdf.addPage();

      const canvas2 = await html2canvas(
        printEventDetailsRef.current,
        captureOptions
      );
      const imgData2 = canvas2.toDataURL("image/jpeg", 0.7);

      pdf.addImage(imgData2, "JPEG", 0, 0, pdfWidth, pdfHeight);

      pdf.save("Call-Sheet.pdf");
    } catch (error) {
      console.error(error);
      toast.error("Failed to generate PDF");
    } finally {
      setIsPrinting(false);
    }
  };

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const handleAddReview = (crewMember: CrewEntry) => {
    setSelectedCrewForReview({
      ...crewMember,
      role: crewMember.assignment?.[0]?.role || "CREW MEMBER",
    });
    setReviewModalOpen(true);
  };

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut" as const,
      },
    },
  };

  const fetchCrewMembers = useCallback(async () => {
    try {
      const res = await apiService.get<CrewEntryResponse>(
        `/api/call-sheets/callsheet/?stream=${id}`
      );

      const timeResponse = await apiService.get<TimeAllocation>(
        `/api/streams/stream/${id}/time-algorithim/`
      );
      const timeJson = timeResponse.data;
      setTimeData({
        call_time: timeJson.call_time || "N/A",
        departure_time: timeJson.departure_time || "N/A",
        arrival_at_venue_time: timeJson.arrival_at_venue_time || "N/A",
        setup_from_time: timeJson.setup_from_time || "N/A",
        setup_to_time: timeJson.setup_to_time || "N/A",
        shoot_start_time: timeJson.shoot_start_time || "N/A",
        shoot_end_time: timeJson.shoot_end_time || "N/A",
        estimated_wrap_time: timeJson.estimated_wrap_time || "N/A",
        back_to_base_time: timeJson.back_to_base_time || "N/A",
        unpacking_time: timeJson.unpacking_time || "N/A",
        depart_time: timeJson.depart_time || "N/A",
      });

      const crewData = res.data.results as CrewEntry[];
      setCrewMembers(crewData);
    } catch (error) {
      console.error("Error fetching crew members:", error);
      setCrewMembers([]);
    }
  }, [id]);

  useEffect(() => {
    const fetchCallSheet = async () => {
      try {
        const response = await apiService.get<IExpandedCallSheetData>(
          `/api/streams/stream/${id}/`
        );

        toast.success("Callsheet data fetched successfully.");
        setCallSheetData(response.data);
        fetchCrewMembers();
      } catch (error) {
        console.error("Failed to fetch call sheet", error);
        toast.error(`Failed to fetch call sheet: ${error}`);
        setCallSheetData(null);
      }
    };

    if (id) {
      fetchCallSheet();
      fetchCrewMembers();
    }
  }, [fetchCrewMembers, id]);

  const completeAddCrew = async (
    user: IUser,
    roleIds: number[],
    resolvedRate: AssignmentRateResolution,
  ) => {
    const stream_id = Number(id);
    const crew_member_id = user.id;
    interface AssignmentResponseProps {
      crew_member: {
        full_name: string;
      };
      id: number;
    }
    const assignment = roleIds.map((roleId) => ({
      role: roleOptions.find((rl) => rl.id === roleId)?.name ?? "CREW MEMBER",
      notes: "",
    }));
    try {
      const payload = {
        stream_id: stream_id,
        crew_member_id: crew_member_id,
        roles_ids: roleIds,
        assignment,
      };
      const asgn = await apiService.post<AssignmentResponseProps>(
        "/api/call-sheets/callsheet/",
        payload
      );

      await createPixelPilotJobAndGig({
        amount: resolvedRate.amount,
        assignedTo: user.user?.id ?? null,
        callsheetId: asgn.data.id,
        crewMemberId: crew_member_id,
        currency: resolvedRate.currency,
        description: callSheetData?.title,
        stream: {
          id: stream_id,
          title: callSheetData?.title ?? "",
          date: String(callSheetData?.date ?? ""),
          start: callSheetData?.start ?? "",
          end: callSheetData?.end ?? "",
        },
      });

    } catch (error: unknown) {
      console.error(`Error adding ${user.user.first_name}`, error);
      let errorMessage = `Failed to add ${user.user.first_name}.`;
      const errorResponse = (error as {
        response?: {
          data?: {
            detail?: string;
            non_field_errors?: string[];
            [key: string]: unknown;
          };
        };
      })?.response;
      if (errorResponse?.data) {
        if (typeof errorResponse.data === "string") {
          errorMessage = errorResponse.data;
        } else if (errorResponse.data.detail) {
          errorMessage = errorResponse.data.detail;
        } else if (errorResponse.data.non_field_errors) {
          errorMessage = errorResponse.data.non_field_errors.join(", ");
        } else if (Object.keys(errorResponse.data).length > 0) {
          errorMessage = Object.entries(errorResponse.data)
            .map(
              ([key, value]) =>
                `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
            )
            .join("; ");
        }
      }
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleAddCrew = async (user: IUser, roleIds: number[]) => {
    if (roleIds.length === 0) {
      toast.error("Please select a role before assigning crew.");
      return false;
    }

    const primaryRoleId = roleIds[0];
    const primaryRole = roleOptions.find((role) => role.id === primaryRoleId);

    if (!primaryRole?.id || !primaryRole?.name) {
      toast.error("Please select a valid role before assigning crew.");
      return false;
    }

    try {
      const hasRate = await ensureRateForRole(
        {
          id: primaryRole.id,
          name: primaryRole.name,
          description: primaryRole.description,
        },
        async (resolvedRate) => {
          await completeAddCrew(user, roleIds, resolvedRate);
        },
        {
          effectiveDate: String(callSheetData?.date ?? ""),
        },
      );

      return hasRate;
    } catch (error) {
      console.error("Failed to validate the selected role rate:", error);
      toast.error("Failed to validate the selected role rate. Please try again.");
      return false;
    }
  };

  const handleDeleteCrew = async (entryId: number) => {
    try {
      await apiService.delete(`/api/call-sheets/callsheet/${entryId}/`);
      toast.success("Crew member removed successfully!");
      await fetchCrewMembers();
    } catch (error: unknown) {
      console.error("Error removing crew member:", error);
      let errorMessage = "Failed to remove crew member.";
      const errorResponse = (error as { response?: { data?: { detail?: string } } })
        ?.response;
      if (errorResponse?.data?.detail) {
        errorMessage = errorResponse.data.detail;
      }
      toast.error(errorMessage);
    }
  };

  const handleShare = async () => {
    if (navigator.share && callSheetData?.website_stream_link) {
      try {
        await navigator.share({
          title: `${callSheetData?.booking?.deceased[0].first_name} ${callSheetData?.booking?.deceased[0].surname} - Memorial Service`,
          url: callSheetData.website_stream_link,
        });
      } catch (error) {
        console.error("Failed to share call sheet", error);
      }
    }
  };

  if (!callSheetData) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Typography>Booking not found.</Typography>
      </motion.div>
    );
  }

  return (
    <>
      {/* Hidden Printable Components */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
        <PrintableCallSheet
          ref={printRef}
          callSheetData={callSheetData}
          crewMembers={crewMembers}
          timeData={timeData}
          reduxTheme={reduxTheme}
          specialRequest={
            callSheetData?.booking?.services[0]?.special_request || "N/A"
          }
        />
        <PrintableEventDetails
          ref={printEventDetailsRef}
          callSheetData={callSheetData}
          eventDetails={eventDetails}
          reduxTheme={reduxTheme}
        />
      </div>

      {/* Main Display Component */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{
          color: colors.primary[400],
          width: "210mm",
          margin: "0 auto",
          minHeight: "297mm",
        }}
      >
        {/* Header Actions */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mt: 2,
              mb: 5,
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(1)}
                style={{
                  padding: "8px 16px",
                  backgroundColor:
                    currentPage === 1
                      ? colors.primary[400]
                      : colors.primary[800],
                  color:
                    currentPage === 1
                      ? colors.primary[900]
                      : colors.primary[100],
                  border: `1px solid ${colors.primary[800]}`,
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                Page 1-Crew Assignment
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(2)}
                style={{
                  padding: "8px 16px",
                  backgroundColor:
                    currentPage === 2
                      ? colors.primary[400]
                      : colors.primary[800],
                  color:
                    currentPage === 2
                      ? colors.primary[900]
                      : colors.primary[100],
                  border: `1px solid ${colors.primary[800]}`,
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                Page 2 - Event Notes
              </motion.button>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <IconButton
                  onClick={handlePrintClick}
                  disabled={isPrinting}
                  sx={{
                    backgroundColor: colors.primary[800],
                    color: colors.primary[100],
                    mr: 2,
                    width: 40,
                    height: 40,
                  }}
                >
                  {isPrinting ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    <PrintIcon />
                  )}
                </IconButton>
              </motion.div>
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <IconButton
                  onClick={handleShare}
                  disabled
                  sx={{
                    backgroundColor: colors.primary[800],
                    color: colors.primary[100],
                    mr: 2,
                  }}
                >
                  <ShareIcon />
                </IconButton>
              </motion.div>
            </Box>
          </Box>
        </motion.div>

        {/* Page Content */}
        <AnimatePresence mode="wait">
          {currentPage === 1 && (
            <motion.div
              key="page1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Main Content */}
              <motion.div variants={itemVariants}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 1,
                    overflow: "hidden",
                    maxWidth: 1200,
                  }}
                >
                  <Grid container spacing={2}>
                    {/* Left Section */}
                    <Grid
                      size={{ xs: 12, md: 4 }}
                      sx={{
                        borderRight: {
                          md: `1px solid ${colors.grey[500]}`,
                        },
                      }}
                    >
                      <CallSheetHeader
                        callSheetData={callSheetData ?? {}}
                        crewMembers={crewMembers ?? []}
                      />
                    </Grid>

                    {/* Middle Section */}
                    <Grid
                      size={{ xs: 12, md: 4 }}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        borderRight: {
                          md: `1px solid ${colors.grey[500]}`,
                        },
                      }}
                    >
                      <CallSheetMainInfo
                        callSheetData={callSheetData}
                        reduxTheme={reduxTheme}
                      />
                    </Grid>

                    {/* Right Section */}
                    <Grid size={{ xs: 12, md: 4 }}>
                      <CallSheetSchedule
                        callSheetData={callSheetData ?? {}}
                        timeData={timeData}
                        reduxTheme={reduxTheme}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </motion.div>

              {/* Crew Table */}
              <motion.div variants={itemVariants}>
                <CrewTable
                  crewMembers={crewMembers}
                  colors={colors}
                  reduxTheme={reduxTheme}
                  showAddButton={showAddButton}
                  onAddCrewClick={() => setAssignCrewModalOpen(true)}
                  onMouseEnter={() => setShowAddButton(true)}
                  onMouseLeave={() => setShowAddButton(false)}
                  onDeleteCrew={handleDeleteCrew}
                  onAddReview={handleAddReview}
                />
              </motion.div>

              {/* Client and Location Details */}
              <ClientDetails
                callSheetData={callSheetData}
                reduxTheme={reduxTheme}
                specialRequest={
                  callSheetData?.booking?.services?.[0]?.special_request || "N/A"
                }
              />
            </motion.div>
          )}

          {currentPage === 2 && (
            <motion.div
              key="page2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <EventDetailsPage
                callSheetData={callSheetData}
                reduxTheme={reduxTheme}
                colors={colors}
                eventDetails={eventDetails}
                setEventDetails={setEventDetails}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <AssignCrewModal
          open={assignCrewModalOpen}
          onClose={() => setAssignCrewModalOpen(false)}
          onAssign={handleAddCrew}
          onSuccessClose={fetchCrewMembers}
          availableRoles={roleOptions}
          theme={reduxTheme}
          colors={colors}
          maxAssignments={8}
          currentAssignments={crewMembers.length}
        />
        <ReviewModal
          open={reviewModalOpen}
          onClose={() => setReviewModalOpen(false)}
          crewMember={selectedCrewForReview}
          theme={reduxTheme}
          colors={colors}
          streamId={Number(id)}
          onReviewSubmitted={fetchCrewMembers}
        />
        <MissingRoleRateDialog {...missingRateDialogProps} />
        <AssignmentRateDialog {...assignmentRateDialogProps} />
      </motion.div>
    </>
  );
};

const CallSheetLayout: React.FC<CallSheetLayoutProps> = ({
  booking,
  booking_type,
  sx = {},
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "900px",
        margin: "0 auto",
        backgroundColor: "background.paper",
        borderRadius: 2,
        boxShadow: 0,
        overflow: "hidden",
        ...sx,
      }}
    >
      <CallSheetPage booking={booking} booking_type={booking_type} />
    </Box>
  );
};

export default CallSheetLayout;
